/*
 * File Path: client/src/components/common/DeleteConfirmationModal.js
 * Description: Modal component for confirming deletion by entering a generated code.
 * Developed by: M. YOUCEF Yazid (yazid.youcef@gmail.com)
 * BDAO-SIS Project
 * Version: 1.0.0
 * CreateDate: 2025-05-01
 * UpdateDate: 2025-05-01
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTimes, FaTrashAlt } from 'react-icons/fa'; // Import icons
import LoadingSpinner from './LoadingSpinner'; // Assuming path is correct
import './DeleteConfirmationModal.css'; // Create this CSS file

// Helper to generate a 6-digit code
const generateCode = () => Math.floor(100000 + Math.random() * 900000).toString();

function DeleteConfirmationModal({
    isOpen,
    onClose,
    onConfirm,
    itemName, // Name of the item being deleted (e.g., device name)
    isLoading = false // Pass loading state from parent if needed for the confirm button
}) {
    const { t } = useTranslation();
    const [generatedCode, setGeneratedCode] = useState('');
    const [enteredCode, setEnteredCode] = useState('');
    const [isCodeCorrect, setIsCodeCorrect] = useState(false);
    const inputRef = useRef(null); // Ref for focusing the input

    // Generate code and focus input when modal opens
    useEffect(() => {
        if (isOpen) {
            const newCode = generateCode();
            setGeneratedCode(newCode);
            setEnteredCode(''); // Reset input field
            setIsCodeCorrect(false); // Reset correctness
            // Focus the input field shortly after the modal opens
            setTimeout(() => {
                inputRef.current?.focus();
            }, 100); // Small delay to ensure modal is rendered
        }
    }, [isOpen]); // Dependency on isOpen

    // Handle input change and check code
    const handleInputChange = (event) => {
        const value = event.target.value;
        // Allow only digits and limit length
        if (/^\d*$/.test(value) && value.length <= 6) {
            setEnteredCode(value);
            setIsCodeCorrect(value === generatedCode); // Check if entered code matches
        }
    };

    // Handle confirmation
    const handleConfirm = () => {
        if (isCodeCorrect) {
            onConfirm(); // Call the parent's confirm function
        }
    };

    // Prevent rendering if not open
    if (!isOpen) {
        return null;
    }

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content delete-modal" onClick={e => e.stopPropagation()}>
                <button className="modal-close-button" onClick={onClose} title={t('common.close', 'Close')}>
                    <FaTimes />
                </button>
                <h2>{t('deleteModal.title', 'Confirm Deletion')}</h2>
                <p>
                    {t('deleteModal.message', `To confirm deletion of "${itemName}", please type the following code:`)}
                </p>
                {/* Display the generated code */}
                <div className="generated-code-display">
                    {generatedCode}
                </div>
                {/* Input for user */}
                <div className="form-group">
                    <label htmlFor="deleteConfirmCode">{t('deleteModal.enterCodeLabel', 'Enter Code:')}</label>
                    <input
                        ref={inputRef} // Assign ref
                        type="text" // Use text to allow seeing input, pattern restricts
                        id="deleteConfirmCode"
                        value={enteredCode}
                        onChange={handleInputChange}
                        placeholder="######"
                        maxLength="6"
                        pattern="\d{6}" // HTML5 pattern validation (optional)
                        className={`delete-code-input ${enteredCode.length > 0 && !isCodeCorrect ? 'input-error' : ''}`}
                        autoComplete="off" // Prevent browser autocomplete
                    />
                </div>

                {/* Action Buttons */}
                <div className="modal-actions">
                    <button
                        onClick={handleConfirm}
                        className="form-action-button delete-button" // Reuse styles if applicable
                        disabled={!isCodeCorrect || isLoading} // Disable if code is wrong or parent is loading
                    >
                        {isLoading ? <LoadingSpinner size="extrasmall" colorOverride="#fff" /> : <FaTrashAlt />}
                        {t('common.delete', 'Delete')} {itemName}
                    </button>
                    <button
                        onClick={onClose}
                        type="button"
                        className="form-action-button cancel-button" // Reuse styles if applicable
                        disabled={isLoading}
                    >
                        {t('common.cancel', 'Cancel')}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default DeleteConfirmationModal;

